<template>
  <head>
    <meta charset="utf-8"/>
    <title>Desk Birdy | Business Permits Performance Dashboard</title>
    <meta content="width=device-width, initial-scale=1.0" name="viewport">
    <meta content="Performance analytics and metrics for business permit applications" name="description"/>
    <meta content="Kelvin Njuguna" name="author"/>
    <link href="assets/images/favicon.svg" rel="shortcut icon">
    <link href="assets/libs/dropzone/min/dropzone.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
    <link href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css" rel="stylesheet">
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
    <link href="./assets/libs/owl.carousel/assets/owl.carousel.min.css" rel="stylesheet">
    <link href="./assets/libs/owl.carousel/assets/owl.theme.default.min.css" rel="stylesheet">
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
  </head>

  <body @contextmenu.prevent data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">
                  <i class="bx bx-line-chart text-primary me-2"></i>
                  Business Permits Performance Dashboard
                </h4>
                <div class="page-title-right">
                  <button @click="refreshData" class="btn btn-success btn-sm me-2" :disabled="loading">
                    <i class="bx bx-refresh"></i> Refresh Data
                  </button>
                  <button @click="exportToCSV" class="btn btn-primary btn-sm" :disabled="isExporting">
                    <i class="bx bx-download me-2"></i>
                    {{ isExporting ? 'Exporting...' : 'Export to CSV' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ToolBar Component -->
          <ToolBar/>

          <!-- Date Filter Section -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card shadow-sm">
                <div class="card-body">
                  <div class="row align-items-end">
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">Date From</label>
                      <input v-model="dateFrom" class="form-control" type="date">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">Date To</label>
                      <input v-model="dateTo" class="form-control" type="date">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">Sub County</label>
                      <select @change="selectedSubCounty($event)" class="form-control">
                        <option value="">All Sub Counties</option>
                        <option :key="index" :value="JSON.stringify(item)" v-for="(item, index) in subCounties">
                          {{ item.subCountyName }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <button @click="applyFilters" class="btn btn-primary w-100" :disabled="loading">
                        <i class="bx bx-filter me-2"></i>Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-Powered Insights Section -->
          <div class="row mb-4" v-if="!loading && aiInsights">
            <div class="col-12">
              <div class="card shadow-lg border-0 ai-insights-card">
                <div class="card-header bg-gradient-primary text-white">
                  <div class="d-flex align-items-center">
                    <i class="bx bx-brain bx-tada font-size-24 me-2"></i>
                    <div>
                      <h5 class="mb-0 text-white">AI-Powered Performance Insights</h5>
                      <small>Intelligent analysis of your permit processing performance</small>
                    </div>
                  </div>
                </div>
                <div class="card-body p-4">

                  <!-- Overall Performance Score -->
                  <div class="row mb-4">
                    <div class="col-12">
                      <div class="performance-score-card">
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 class="text-muted mb-1">OVERALL SYSTEM HEALTH</h6>
                            <h2 class="mb-0" :class="getHealthScoreColor()">
                              {{ aiInsights.overallScore }}/100
                              <span class="font-size-16 ms-2">{{ getHealthStatus() }}</span>
                            </h2>
                          </div>
                          <div class="text-end">
                            <i :class="getHealthIcon()" class="font-size-48" :style="{color: getHealthIconColor()}"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Key Insights Grid -->
                  <div class="row mb-4">
                    <!-- Critical Issue -->
                    <div class="col-lg-4 mb-3" v-if="aiInsights.criticalIssue">
                      <div class="insight-card critical">
                        <div class="insight-icon">
                          <i class="bx bxs-error-circle"></i>
                        </div>
                        <div class="insight-content">
                          <h6 class="text-danger mb-2">🚨 CRITICAL ISSUE</h6>
                          <p class="mb-2">{{ aiInsights.criticalIssue.message }}</p>
                          <small class="text-muted">{{ aiInsights.criticalIssue.impact }}</small>
                        </div>
                      </div>
                    </div>

                    <!-- Most Notorious Bottleneck -->
                    <div class="col-lg-4 mb-3" v-if="aiInsights.notoriousBottleneck">
                      <div class="insight-card warning">
                        <div class="insight-icon">
                          <i class="bx bxs-traffic-cone"></i>
                        </div>
                        <div class="insight-content">
                          <h6 class="text-warning mb-2">⚠️ MAJOR BOTTLENECK</h6>
                          <p class="mb-2">{{ aiInsights.notoriousBottleneck.location }}</p>
                          <small class="text-muted">{{ aiInsights.notoriousBottleneck.stats }}</small>
                        </div>
                      </div>
                    </div>

                    <!-- Top Performer -->
                    <div class="col-lg-4 mb-3" v-if="aiInsights.topPerformer">
                      <div class="insight-card success">
                        <div class="insight-icon">
                          <i class="bx bxs-trophy"></i>
                        </div>
                        <div class="insight-content">
                          <h6 class="text-success mb-2">🏆 TOP PERFORMER</h6>
                          <p class="mb-2">{{ aiInsights.topPerformer.name }}</p>
                          <small class="text-muted">{{ aiInsights.topPerformer.achievement }}</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- AI Recommendations -->
                  <div class="recommendations-section">
                    <h6 class="text-uppercase text-muted mb-3">
                      <i class="bx bx-bulb me-2"></i>
                      Actionable Recommendations
                    </h6>
                    <div class="row">
                      <div class="col-lg-6 mb-3" v-for="(recommendation, index) in aiInsights.recommendations" :key="index">
                        <div class="recommendation-card">
                          <div class="d-flex align-items-start">
                            <div class="recommendation-number">{{ index + 1 }}</div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-2">{{ recommendation.title }}</h6>
                              <p class="text-muted mb-2">{{ recommendation.description }}</p>
                              <div class="recommendation-meta">
                                <span class="badge" :class="getPriorityBadge(recommendation.priority)">
                                  {{ recommendation.priority }} Priority
                                </span>
                                <span class="badge bg-info-subtle text-info ms-2">
                                  <i class="bx bx-trending-up"></i> {{ recommendation.expectedImpact }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Quick Stats -->
                  <div class="row mt-4">
                    <div class="col-md-3">
                      <div class="quick-stat">
                        <div class="stat-value text-danger">{{ aiInsights.stats.criticalCount }}</div>
                        <div class="stat-label">Critical Delays</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="quick-stat">
                        <div class="stat-value text-warning">{{ aiInsights.stats.avgDelayDays }}</div>
                        <div class="stat-label">Avg Delay (Days)</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="quick-stat">
                        <div class="stat-value text-info">{{ aiInsights.stats.efficiencyRate }}%</div>
                        <div class="stat-label">System Efficiency</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="quick-stat">
                        <div class="stat-value text-success">{{ aiInsights.stats.onTimeRate }}%</div>
                        <div class="stat-label">On-Time Rate</div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="row">
            <div class="col-12">
              <div class="card shadow-sm">
                <div class="card-body text-center py-5">
                  <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <h5>Loading Performance Data...</h5>
                  <p class="text-muted">Please wait while we fetch the analytics</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Dashboard Content -->
          <div v-else class="performance-dashboard">

            <!-- Ranking Explanation Banner -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="alert alert-info border-0 shadow-sm d-flex align-items-center">
                  <i class="bx bx-info-circle font-size-24 me-3"></i>
                  <div>
                    <h6 class="mb-1 fw-bold">Sub-County Ranking System</h6>
                    <p class="mb-0 text-sm">
                      Sub-counties are ranked by <strong>completion performance</strong>:
                      <span class="badge bg-warning text-dark mx-1"><i class="bx bx-trophy"></i> #1 BEST</span> = Highest completion rate & most approved applications |
                      <span class="badge bg-danger mx-1"><i class="bx bx-error-circle"></i> BOTTOM 3</span> = Lowest completion rate & most pending applications
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Critical Alerts Section -->
            <div class="row mb-4" v-if="stuckApplications.length > 0">
              <div class="col-12">
                <div class="card border-danger shadow-lg">
                  <div class="card-header bg-danger text-white">
                    <h5 class="mb-0 text-white">
                      <i class="bx bx-error-circle bx-tada me-2"></i>
                      CRITICAL ALERTS: {{ stuckApplications.length }} Applications Delayed Beyond 24 Hours
                    </h5>
                  </div>
                  <div class="card-body" style="max-height: 400px; overflow-y: auto;">
                    <div v-for="(app, index) in stuckApplications" :key="index" class="stuck-alert">
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="flex-grow-1">
                          <h6 class="text-danger mb-2">
                            <i class="bx bx-time-five me-1"></i>
                            {{ formatDaysStuck(app.daysStuck) }} - {{ app.businessName }}
                          </h6>
                          <div class="row text-sm">
                            <div class="col-md-3">
                              <strong>Bill No:</strong> {{ app.billNo }}<br>
                              <strong>Business ID:</strong> {{ app.businessID }}
                            </div>
                            <div class="col-md-3">
                              <strong>Business Name:</strong> {{ app.businessName }}<br>
                              <strong>Sub County:</strong> {{ app.subCountyName }}
                            </div>
                            <div class="col-md-3">
                              <strong>Stage:</strong>
                              <span class="badge" :class="getStageBadgeColor(app.statusID)">
                                {{ getStageNameDisplay(app.statusID) }}
                              </span><br>
                              <strong>Ward:</strong> {{ app.wardName }}
                            </div>
                            <div class="col-md-3">
                              <strong>Stuck Since:</strong> {{ formattedDate(app.dateCreated) }}<br>
                              <span class="badge" :class="getDangerLevel(app.hoursStuck)">
                                {{ formatStuckDuration(app.hoursStuck) }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Overall Performance Metrics - All 5 Stages -->
            <div class="row mb-4">
              <div class="col-xl col-lg-4 col-md-6 mb-3" v-for="(stage, index) in stagePerformance" :key="index">
                <div class="card performance-card shadow" :class="getStageStatusClass(stage)">
                  <div class="card-body position-relative">
                    <i :class="getStageIcon(stage.statusID)" class="stat-icon"></i>
                    <h6 class="text-uppercase mb-3 opacity-90">
                      Stage {{ stage.statusID }}: {{ getStageNameDisplay(stage.statusID) }}
                    </h6>
                    <div class="metric-badge mb-2">{{ stage.totalApps || 0 }}</div>
                    <div class="sub-metric mb-3">
                      <i class="bx bx-time me-1"></i>Avg: {{ formatHours(stage.avgHours) }}
                    </div>

                    <div class="progress mb-2" style="height: 8px;">
                      <div class="progress-bar"
                           :class="getProgressClass(stage)"
                           :style="{ width: getCompletionPercentage(stage) + '%' }">
                      </div>
                    </div>

                    <div class="row text-sm sub-metric">
                      <div class="col-6">
                        <i class="bx bx-check-circle me-1"></i>
                        On Time: {{ stage.within24Hours || 0 }}
                      </div>
                      <div class="col-6">
                        <i class="bx bx-x-circle me-1"></i>
                        Overdue: {{ stage.over24Hours || 0 }}
                      </div>
                    </div>

                    <div v-if="stage.totalApps === 0" class="mt-2">
                      <span class="badge bg-success-subtle text-success">
                        <i class="bx bx-check"></i> All on-time
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sub-County Performance Ranking with Stage Breakdown -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card shadow">
                  <div class="card-header bg-primary text-white">
                    <h5 class="mb-0 text-white">
                      <i class="bx bx-trophy me-2"></i>
                      Sub-County Performance Ranking with Stage Breakdown
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-12" v-for="(county, index) in subCountyPerformance" :key="index">
                        <div class="subcounty-rank mb-4" :class="getRankClass(index)">
                          <!-- Header -->
                          <div class="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 class="mb-1">
                                <span v-if="index === 0" class="badge bg-warning text-dark me-2" style="font-size: 1rem;">
                                  <i class="bx bx-trophy"></i> #1 BEST
                                </span>
                                <span v-else-if="index === 1" class="badge bg-secondary me-2" style="font-size: 1rem;">
                                  <i class="bx bx-medal"></i> #2
                                </span>
                                <span v-else-if="index === 2" class="badge bg-info me-2" style="font-size: 1rem;">
                                  <i class="bx bx-medal"></i> #3
                                </span>
                                <span v-else-if="index >= subCountyPerformance.length - 3"
                                      class="badge bg-danger me-2" style="font-size: 1rem;">
                                  <i class="bx bx-error-circle"></i> #{{ index + 1 }} NEEDS IMPROVEMENT
                                </span>
                                <span v-else class="badge bg-secondary me-2" style="font-size: 1rem;">
                                  #{{ index + 1 }}
                                </span>
                                {{ county.subCountyName }}
                              </h5>
                              <div class="text-sm">
                                <span class="badge" :class="getPerformanceBadge(county.completionRate)">
                                  {{ county.completionRate || 0 }}% Completion Rate
                                </span>
                                <span class="badge bg-info ms-2">
                                  {{ county.sameDayPercentage || 0 }}% Same-Day Service
                                </span>
                                <span v-if="county.pendingApps > 0" class="badge bg-warning text-dark ms-2">
                                  <i class="bx bx-time"></i> {{ county.pendingApps }} Pending
                                </span>
                              </div>
                            </div>
                            <div class="text-end">
                              <div class="metric-badge text-dark" style="font-size: 1.5rem;">
                                {{ county.totalApplications }}
                              </div>
                              <small class="text-muted">Total Apps</small>
                              <div class="mt-1">
                                <span class="badge bg-success">{{ county.completedApps }} ✓</span>
                                <span v-if="county.pendingApps > 0" class="badge bg-danger ms-1">
                                  {{ county.pendingApps }} ⏳
                                </span>
                              </div>
                            </div>
                          </div>

                          <!-- Stage Breakdown -->
                          <div class="card border-0 bg-light mb-3">
                            <div class="card-body p-3">
                              <h6 class="mb-3 text-uppercase text-muted" style="font-size: 0.75rem; font-weight: 600;">
                                <i class="bx bx-git-branch me-1"></i>
                                Applications by Stage
                              </h6>

                              <div class="row g-2">
                                <!-- Stage 1: SBP Application -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded"
                                       :class="getStageBoxClass(county.stage1_overdue, county.stage1_total)">
                                    <div class="stage-label text-uppercase" style="font-size: 0.65rem; font-weight: 600;">
                                      Stage 1
                                    </div>
                                    <div class="stage-name mb-1" style="font-size: 0.7rem;">
                                      SBP Application
                                    </div>
                                    <div class="stage-count" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.stage1_total || 0 }}
                                    </div>
                                    <div v-if="county.stage1_overdue > 0" class="stage-overdue mt-1">
                                      <span class="badge bg-danger" style="font-size: 0.65rem;">
                                        {{ county.stage1_overdue }} overdue
                                      </span>
                                    </div>
                                    <div v-else class="stage-overdue mt-1">
                                      <span class="badge bg-success" style="font-size: 0.65rem;">
                                        All on-time
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Stage 2: Bill Payment -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded"
                                       :class="getStageBoxClass(county.stage2_overdue, county.stage2_total)">
                                    <div class="stage-label text-uppercase" style="font-size: 0.65rem; font-weight: 600;">
                                      Stage 2
                                    </div>
                                    <div class="stage-name mb-1" style="font-size: 0.7rem;">
                                      Bill Payment
                                    </div>
                                    <div class="stage-count" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.stage2_total || 0 }}
                                    </div>
                                    <div v-if="county.stage2_overdue > 0" class="stage-overdue mt-1">
                                      <span class="badge bg-danger" style="font-size: 0.65rem;">
                                        {{ county.stage2_overdue }} overdue
                                      </span>
                                    </div>
                                    <div v-else class="stage-overdue mt-1">
                                      <span class="badge bg-success" style="font-size: 0.65rem;">
                                        All on-time
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Stage 3: Application Validation -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded"
                                       :class="getStageBoxClass(county.stage3_overdue, county.stage3_total)">
                                    <div class="stage-label text-uppercase" style="font-size: 0.65rem; font-weight: 600;">
                                      Stage 3
                                    </div>
                                    <div class="stage-name mb-1" style="font-size: 0.7rem;">
                                      Validation
                                    </div>
                                    <div class="stage-count" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.stage3_total || 0 }}
                                    </div>
                                    <div v-if="county.stage3_overdue > 0" class="stage-overdue mt-1">
                                      <span class="badge bg-danger" style="font-size: 0.65rem;">
                                        {{ county.stage3_overdue }} overdue
                                      </span>
                                    </div>
                                    <div v-else class="stage-overdue mt-1">
                                      <span class="badge bg-success" style="font-size: 0.65rem;">
                                        All on-time
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Stage 4: Inspection -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded"
                                       :class="getStageBoxClass(county.stage4_overdue, county.stage4_total)">
                                    <div class="stage-label text-uppercase" style="font-size: 0.65rem; font-weight: 600;">
                                      Stage 4
                                    </div>
                                    <div class="stage-name mb-1" style="font-size: 0.7rem;">
                                      Inspection
                                    </div>
                                    <div class="stage-count" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.stage4_total || 0 }}
                                    </div>
                                    <div v-if="county.stage4_overdue > 0" class="stage-overdue mt-1">
                                      <span class="badge bg-danger" style="font-size: 0.65rem;">
                                        {{ county.stage4_overdue }} overdue
                                      </span>
                                    </div>
                                    <div v-else class="stage-overdue mt-1">
                                      <span class="badge bg-success" style="font-size: 0.65rem;">
                                        All on-time
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Stage 5: Approved -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded bg-success-subtle border border-success">
                                    <div class="stage-label text-uppercase text-success" style="font-size: 0.65rem; font-weight: 600;">
                                      Stage 5
                                    </div>
                                    <div class="stage-name mb-1 text-success" style="font-size: 0.7rem;">
                                      Approved
                                    </div>
                                    <div class="stage-count text-success" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.stage5_total || 0 }}
                                    </div>
                                    <div class="stage-overdue mt-1">
                                      <span class="badge bg-success" style="font-size: 0.65rem;">
                                        <i class="bx bx-check"></i> Completed
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <!-- Summary Stats -->
                                <div class="col-md-2 col-4">
                                  <div class="stage-box p-2 text-center rounded border">
                                    <div class="stage-label text-uppercase text-muted" style="font-size: 0.65rem; font-weight: 600;">
                                      Summary
                                    </div>
                                    <div class="stage-name mb-1" style="font-size: 0.7rem;">
                                      Overall
                                    </div>
                                    <div class="stage-count text-primary" style="font-size: 1.25rem; font-weight: 700;">
                                      {{ county.totalApplications }}
                                    </div>
                                    <div class="stage-overdue mt-1">
                                      <span v-if="county.overdueApps > 0" class="badge bg-warning text-dark" style="font-size: 0.65rem;">
                                        {{ county.overdueApps }} overdue
                                      </span>
                                      <span v-else class="badge bg-info" style="font-size: 0.65rem;">
                                        All on track
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Bottom Stats Row -->
                          <div class="row mt-3 text-sm">
                            <div class="col-md-3 col-6 mb-2">
                              <i class="bx bx-check-circle text-success me-1"></i>
                              <strong>{{ county.completedApps }}</strong> Completed
                            </div>
                            <div class="col-md-3 col-6 mb-2">
                              <i class="bx bx-time text-warning me-1"></i>
                              <strong>{{ county.pendingApps }}</strong> Pending
                            </div>
                            <div class="col-md-3 col-6 mb-2">
                              <i class="bx bx-error text-danger me-1"></i>
                              <strong>{{ county.overdueApps }}</strong> Overdue
                            </div>
                            <div class="col-md-3 col-6 mb-2">
                              <i class="bx bx-stopwatch text-info me-1"></i>
                              <strong>{{ formatHours(county.avgCompletionHours) }}</strong> Avg Time
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stage Progress Timeline -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card shadow">
                  <div class="card-header bg-info text-white">
                    <h5 class="mb-0 text-white">
                      <i class="bx bx-list-check me-2"></i>
                      Stage-wise Performance
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-for="(stage, index) in stagePerformance" :key="index" class="timeline-stage">
                      <div class="timeline-dot" :class="getTimelineDotClass(stage)"></div>
                      <div>
                        <h6 class="mb-1">Stage {{ stage.statusID }}: {{ getStageNameDisplay(stage.statusID) }}</h6>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="badge" :class="getStageBadgeClass(stage)">
                            {{ stage.totalApps }} Applications
                          </span>
                          <span class="text-sm text-muted">
                            Avg: {{ formatHours(stage.avgHours) }}
                          </span>
                        </div>
                        <div class="progress" style="height: 6px;">
                          <div class="progress-bar bg-success"
                               :style="{ width: getOnTimePercentage(stage) + '%' }">
                          </div>
                          <div class="progress-bar bg-danger"
                               :style="{ width: getOverduePercentage(stage) + '%' }">
                          </div>
                        </div>
                        <div class="text-xs mt-1">
                          <span class="text-success">{{ stage.within24Hours || 0 }} on-time</span>
                          <span class="text-danger ms-2">{{ stage.over24Hours || 0 }} overdue</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Daily Trend -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card shadow">
                  <div class="card-header bg-success text-white">
                    <h5 class="mb-0 text-white">
                      <i class="bx bx-trending-up me-2"></i>
                      Daily Performance Trend (Last 30 Days)
                    </h5>
                  </div>
                  <div class="card-body">
                    <!-- Summary Stats Row -->
                    <div class="row mb-4">
                      <div class="col-md-3">
                        <div class="trend-stat-card bg-primary-subtle">
                          <div class="trend-stat-icon text-primary">
                            <i class="bx bx-receipt"></i>
                          </div>
                          <div class="trend-stat-content">
                            <h3 class="mb-0">{{ calculateTotalApplications() }}</h3>
                            <p class="text-muted mb-0">Total Applications</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="trend-stat-card bg-success-subtle">
                          <div class="trend-stat-icon text-success">
                            <i class="bx bx-check-double"></i>
                          </div>
                          <div class="trend-stat-content">
                            <h3 class="mb-0">{{ calculateTotalCompletions() }}</h3>
                            <p class="text-muted mb-0">Same-Day Completions</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="trend-stat-card bg-info-subtle">
                          <div class="trend-stat-icon text-info">
                            <i class="bx bx-time"></i>
                          </div>
                          <div class="trend-stat-content">
                            <h3 class="mb-0">{{ calculateAvgCompletionTime() }}</h3>
                            <p class="text-muted mb-0">Avg Completion Time</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="trend-stat-card bg-warning-subtle">
                          <div class="trend-stat-icon text-warning">
                            <i class="bx bx-line-chart"></i>
                          </div>
                          <div class="trend-stat-content">
                            <h3 class="mb-0">{{ calculateOverallEfficiency() }}%</h3>
                            <p class="text-muted mb-0">Overall Efficiency</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Daily Breakdown Table -->
                    <div class="table-responsive">
                      <table class="table table-hover align-middle">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th class="text-center">Total Apps</th>
                          <th class="text-center">Same-Day</th>
                          <th class="text-center">Completion Rate</th>
                          <th class="text-center">Avg Time</th>
                          <th>Efficiency</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(day, index) in dailyTrend.slice(0, 30)" :key="index"
                            :class="getRowHighlight(day)">
                          <td>
                            <strong>{{ formatDateShort(day.applicationDate) }}</strong>
                            <br>
                            <small class="text-muted">{{ getDayOfWeek(day.applicationDate) }}</small>
                          </td>
                          <td class="text-center">
                              <span class="badge" :class="getApplicationCountBadge(day.totalApps)">
                                {{ day.totalApps }}
                              </span>
                          </td>
                          <td class="text-center">
                              <span class="badge bg-success">
                                {{ day.sameDayCompletion || 0 }}
                              </span>
                          </td>
                          <td class="text-center">
                              <span class="badge" :class="getCompletionRateBadge(day)">
                                {{ calculateDayCompletionRate(day) }}%
                              </span>
                          </td>
                          <td class="text-center">
                            <span class="text-muted">{{ formatHours(day.avgCompletionHours) }}</span>
                          </td>
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="progress flex-grow-1" style="height: 20px; min-width: 100px;">
                                <div class="progress-bar"
                                     :class="getEfficiencyClass(day)"
                                     :style="{ width: calculateEfficiency(day) + '%' }"
                                     role="progressbar">
                                  <small class="fw-bold">{{ calculateEfficiency(day) }}%</small>
                                </div>
                              </div>
                              <span class="ms-2">
                                  <i v-if="calculateEfficiency(day) >= 80" class="bx bx-trending-up text-success"></i>
                                  <i v-else-if="calculateEfficiency(day) >= 50" class="bx bx-minus text-warning"></i>
                                  <i v-else class="bx bx-trending-down text-danger"></i>
                                </span>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Show message if no data -->
                    <div v-if="!dailyTrend || dailyTrend.length === 0" class="text-center py-5">
                      <i class="bx bx-calendar-x font-size-48 text-muted mb-3"></i>
                      <h5 class="text-muted">No trend data available</h5>
                      <p class="text-muted">Data will appear as applications are processed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">County Government</div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>

  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import ToolBar from "@/components/sbp/ToolBar";
import { authUrl, execute, executeDownload, trade } from "@/api";
import moment from 'moment';

export default {
  name: "BusinessPermitsPerformanceDashboard",
  components: { NavigationBar, ToolBar },

  data() {
    return {
      loading: false,
      isExporting: false,

      // Filters
      dateFrom: '',
      dateTo: '',
      selectedSubCountyID: null,

      // Dropdowns
      subCounties: [],

      // Performance Metrics
      performanceMetrics: {},
      stagePerformance: [],
      subCountyPerformance: [],
      stuckApplications: [],
      dailyTrend: [],

      // AI Insights
      aiInsights: null
    };
  },

  mounted() {
    this.dateFrom = moment().startOf('month').format('YYYY-MM-DD');
    this.dateTo = moment().format('YYYY-MM-DD');
    this.getSubCounties();
    this.fetchPerformanceMetrics();
  },

  methods: {
    async fetchPerformanceMetrics() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getApplicationsMetrics");
        data.append("dateFrom", this.dateFrom);
        data.append("dateTo", this.dateTo);
        data.append("subCountyID", this.selectedSubCountyID || 0);

        const res = await execute(data, trade);

        console.log('API Response:', res.data); // Debug log

        if (res.data.success) {
          // Handle both possible response structures
          const metricsData = res.data.data?.performanceMetrics || res.data.performanceMetrics;

          if (metricsData) {
            this.performanceMetrics = metricsData;
            this.stagePerformance = metricsData.byStage || [];
            this.subCountyPerformance = metricsData.bySubCounty || [];
            this.stuckApplications = metricsData.stuckApplications || [];
            this.dailyTrend = metricsData.dailyTrend || [];

            console.log('Loaded data:', {
              stages: this.stagePerformance.length,
              counties: this.subCountyPerformance.length,
              stuck: this.stuckApplications.length,
              trends: this.dailyTrend.length
            });
          } else {
            console.warn('No performance metrics found in response');
          }
        } else {
          console.error('API returned success: false');
        }
      } catch (error) {
        console.error("Error loading performance metrics:", error);
        alert('Failed to load performance data. Please check console for details.');
      } finally {
        this.loading = false;
      }

      // Generate AI Insights after data is loaded
      this.generateAIInsights();
    },

    generateAIInsights() {
      if (!this.stagePerformance.length || !this.subCountyPerformance.length) {
        return;
      }

      // Calculate metrics - EXCLUDING Stage 5 (Approved) as it's the goal state, not a problem
      const pendingStages = this.stagePerformance.filter(s => parseInt(s.statusID) < 5);
      const approvedStage = this.stagePerformance.find(s => parseInt(s.statusID) === 5);

      const totalPendingApps = pendingStages.reduce((sum, stage) => sum + parseInt(stage.totalApps || 0), 0);
      const totalOverdue = pendingStages.reduce((sum, stage) => sum + parseInt(stage.over24Hours || 0), 0);
      const totalOnTime = pendingStages.reduce((sum, stage) => sum + parseInt(stage.within24Hours || 0), 0);
      const totalApproved = approvedStage ? parseInt(approvedStage.totalApps || 0) : 0;
      const totalApps = totalPendingApps + totalApproved;

      // Efficiency based on pending apps only (stages 1-4)
      const efficiencyRate = totalPendingApps > 0 ? Math.round((totalOnTime / totalPendingApps) * 100) : 100;
      // On-time rate includes all apps that reached stage 5
      const onTimeRate = totalApps > 0 ? Math.round((totalApproved / totalApps) * 100) : 0;

      // Find the most notorious bottleneck - ONLY from stages 1-4, NOT Stage 5
      const worstStage = [...pendingStages]
          .filter(s => parseInt(s.totalApps) > 0)
          .sort((a, b) => parseInt(b.over24Hours || 0) - parseInt(a.over24Hours || 0))[0];

      const worstCounty = [...this.subCountyPerformance]
          .sort((a, b) => parseFloat(a.completionRate || 0) - parseFloat(b.completionRate || 0))[0];

      const topCounty = [...this.subCountyPerformance]
          .sort((a, b) => parseFloat(b.completionRate || 0) - parseFloat(a.completionRate || 0))[0];

      const avgDelayHours = this.stuckApplications.length > 0
          ? this.stuckApplications.reduce((sum, app) => sum + parseFloat(app.hoursStuck || 0), 0) / this.stuckApplications.length
          : 0;
      const avgDelayDays = Math.round(avgDelayHours / 24);

      let overallScore = 100;
      // Deduct points based on pending stages (1-4) issues only
      overallScore -= (totalOverdue / Math.max(totalPendingApps, 1)) * 50; // Max -50 for overdue in pending stages
      overallScore -= Math.min(avgDelayDays * 2, 30); // Max -30 for delays
      overallScore -= this.stuckApplications.length > 20 ? 20 : 0; // -20 if >20 stuck apps
      // Bonus for high approval rate
      if (onTimeRate > 70) {
        overallScore += 10; // Bonus for getting apps to Stage 5
      }
      overallScore = Math.max(0, Math.min(100, Math.round(overallScore)));

      const recommendations = [];

      // Recommendation 1: Address critical bottleneck (only for stages 1-4)
      if (worstStage && parseInt(worstStage.statusID) < 5 && parseInt(worstStage.over24Hours) > 10) {
        recommendations.push({
          title: `Urgent: Clear ${this.getStageNameDisplay(worstStage.statusID)} Backlog`,
          description: `${worstStage.over24Hours} applications stuck at this stage for over 24 hours. Allocate additional resources immediately.`,
          priority: 'HIGH',
          expectedImpact: `Could clear ${worstStage.over24Hours} applications`
        });
      }

      if (worstCounty && parseFloat(worstCounty.completionRate) < 50) {
        recommendations.push({
          title: `Deploy Support Team to ${worstCounty.subCountyName}`,
          description: `Only ${worstCounty.completionRate}% completion rate with ${worstCounty.pendingApps} pending applications.`,
          priority: 'HIGH',
          expectedImpact: `Improve ${worstCounty.pendingApps} pending cases`
        });
      }

      if (avgDelayDays > 7) {
        recommendations.push({
          title: 'Implement Fast-Track System',
          description: `Average delay of ${avgDelayDays} days is unacceptable. Create expedited processing for applications over 7 days old.`,
          priority: 'MEDIUM',
          expectedImpact: 'Reduce avg delay by 50%'
        });
      }

      if (topCounty && parseFloat(topCounty.completionRate) > 80) {
        recommendations.push({
          title: `Replicate ${topCounty.subCountyName} Best Practices`,
          description: `${topCounty.completionRate}% completion rate. Document and share their workflow.`,
          priority: 'MEDIUM',
          expectedImpact: 'System-wide improvement'
        });
      }

      if (this.stuckApplications.length > 30) {
        recommendations.push({
          title: 'Automate Validation Process',
          description: `${this.stuckApplications.length} stuck applications suggest manual bottlenecks.`,
          priority: 'LOW',
          expectedImpact: '30% faster processing'
        });
      }

      // Positive recommendation: Celebrate high approval rate
      if (onTimeRate > 70 && totalApproved > 50) {
        recommendations.push({
          title: 'Excellent Approval Performance',
          description: `${onTimeRate}% of applications have reached Stage 5 (Approved). ${totalApproved} businesses are now licensed. Keep this momentum!`,
          priority: 'LOW',
          expectedImpact: 'Maintain current efficiency'
        });
      }

      if (efficiencyRate < 60) {
        recommendations.push({
          title: 'Conduct Emergency Training',
          description: `${efficiencyRate}% efficiency indicates systematic issues.`,
          priority: 'HIGH',
          expectedImpact: 'Improve efficiency by 25%'
        });
      }

      this.aiInsights = {
        overallScore,
        criticalIssue: this.stuckApplications.length > 0 ? {
          message: `${this.stuckApplications.length} applications critically delayed (>24 hours)`,
          impact: `Longest delay: ${Math.round(parseFloat(this.stuckApplications[0]?.hoursStuck || 0) / 24)} days`
        } : null,
        notoriousBottleneck: worstStage ? {
          location: `${this.getStageNameDisplay(worstStage.statusID)} - Stage ${worstStage.statusID}`,
          stats: `${worstStage.over24Hours} of ${worstStage.totalApps} apps overdue`
        } : null,
        topPerformer: topCounty ? {
          name: topCounty.subCountyName,
          achievement: `${topCounty.completionRate}% completion rate`
        } : null,
        recommendations: recommendations.slice(0, 6),
        stats: {
          criticalCount: this.stuckApplications.length,
          avgDelayDays,
          efficiencyRate,
          onTimeRate
        }
      };
    },

    exportToCSV() {
      this.isExporting = true;

      try {
        const csvData = [];

        csvData.push(['BUSINESS PERMITS PERFORMANCE REPORT']);
        csvData.push([`Generated: ${new Date().toLocaleString()}`]);
        csvData.push([`Period: ${this.dateFrom} to ${this.dateTo}`]);
        csvData.push([]);

        if (this.aiInsights) {
          csvData.push(['=== AI INSIGHTS SUMMARY ===']);
          csvData.push(['Overall System Health Score', `${this.aiInsights.overallScore}/100`]);
          csvData.push(['System Status', this.getHealthStatus()]);
          csvData.push(['Critical Delays', this.aiInsights.stats.criticalCount]);
          csvData.push(['Average Delay (Days)', this.aiInsights.stats.avgDelayDays]);
          csvData.push(['System Efficiency', `${this.aiInsights.stats.efficiencyRate}%`]);
          csvData.push([]);

          csvData.push(['=== TOP RECOMMENDATIONS ===']);
          this.aiInsights.recommendations.forEach((rec, index) => {
            csvData.push([`${index + 1}. ${rec.title}`, rec.description, rec.priority]);
          });
          csvData.push([]);
        }

        csvData.push(['=== STAGE PERFORMANCE ===']);
        csvData.push(['Stage ID', 'Stage Name', 'Total Apps', 'Avg Hours', 'On Time', 'Overdue (>24h)']);
        this.stagePerformance.forEach(stage => {
          csvData.push([
            stage.statusID,
            this.getStageNameDisplay(stage.statusID),
            stage.totalApps || 0,
            this.formatHours(stage.avgHours),
            stage.within24Hours || 0,
            stage.over24Hours || 0
          ]);
        });
        csvData.push([]);

        csvData.push(['=== SUB-COUNTY PERFORMANCE ===']);
        csvData.push(['Rank', 'Sub County', 'Total', 'Completed', 'Pending', 'Completion Rate']);
        this.subCountyPerformance.forEach((county, index) => {
          csvData.push([
            index + 1,
            county.subCountyName,
            county.totalApplications,
            county.completedApps,
            county.pendingApps,
            `${county.completionRate}%`
          ]);
        });
        csvData.push([]);

        csvData.push(['=== STUCK APPLICATIONS ===']);
        csvData.push(['Business Name', 'Bill No', 'Sub County', 'Stage', 'Days Stuck']);
        this.stuckApplications.slice(0, 50).forEach(app => {
          csvData.push([
            app.businessName,
            app.billNo,
            app.subCountyName,
            this.getStageNameDisplay(app.statusID),
            Math.round(app.daysStuck)
          ]);
        });

        const csvContent = csvData.map(row =>
            row.map(cell => {
              const cellStr = String(cell || '');
              if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            }).join(',')
        ).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `Performance_Report_${this.dateFrom}_to_${this.dateTo}_${Date.now()}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        alert('CSV exported successfully!');

      } catch (error) {
        console.error('Export error:', error);
        alert('Failed to export CSV.');
      } finally {
        this.isExporting = false;
      }
    },

    getHealthStatus() {
      if (!this.aiInsights) return 'N/A';
      const score = this.aiInsights.overallScore;
      if (score >= 80) return 'EXCELLENT';
      if (score >= 60) return 'GOOD';
      if (score >= 40) return 'NEEDS IMPROVEMENT';
      return 'CRITICAL';
    },

    getHealthScoreColor() {
      if (!this.aiInsights) return 'text-muted';
      const score = this.aiInsights.overallScore;
      if (score >= 80) return 'text-success';
      if (score >= 60) return 'text-info';
      if (score >= 40) return 'text-warning';
      return 'text-danger';
    },

    getHealthIcon() {
      if (!this.aiInsights) return 'bx bx-meh';
      const score = this.aiInsights.overallScore;
      if (score >= 80) return 'bx bx-happy-alt';
      if (score >= 60) return 'bx bx-smile';
      if (score >= 40) return 'bx bx-meh';
      return 'bx bx-sad';
    },

    getHealthIconColor() {
      if (!this.aiInsights) return '#6c757d';
      const score = this.aiInsights.overallScore;
      if (score >= 80) return '#10b981';
      if (score >= 60) return '#3b82f6';
      if (score >= 40) return '#f59e0b';
      return '#ef4444';
    },

    getPriorityBadge(priority) {
      if (priority === 'HIGH') return 'bg-danger';
      if (priority === 'MEDIUM') return 'bg-warning text-dark';
      return 'bg-info';
    },

    refreshData() {
      this.fetchPerformanceMetrics();
    },

    applyFilters() {
      this.fetchPerformanceMetrics();
    },

    async exportReport() {
      this.isExporting = true;
      try {
        // Generate CSV content from current dashboard data
        const csvContent = this.generateCSV();

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `Performance_Report_${this.dateFrom}_to_${this.dateTo}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export failed:", error);
        alert('Failed to export report. Please try again.');
      } finally {
        this.isExporting = false;
      }
    },

    generateCSV() {
      let csv = '';

      // Header Section
      csv += '=== BUSINESS PERMITS PERFORMANCE REPORT ===\n';
      csv += `Report Period: ${this.dateFrom} to ${this.dateTo}\n`;
      csv += `Generated: ${moment().format('YYYY-MM-DD HH:mm:ss')}\n`;
      csv += '\n\n';

      // ========== 1. STAGE PERFORMANCE SUMMARY ==========
      csv += '=== STAGE PERFORMANCE SUMMARY ===\n';
      csv += 'Stage,Stage Name,Total Applications,Avg Hours,Max Hours,Min Hours,On-Time (<24h),Overdue (>24h),Overdue (>48h),Overdue (>72h)\n';

      this.stagePerformance.forEach(stage => {
        csv += `${stage.statusID},`;
        csv += `"${this.getStageNameDisplay(stage.statusID)}",`;
        csv += `${stage.totalApps || 0},`;
        csv += `${stage.avgHours || 0},`;
        csv += `${stage.maxHours || 0},`;
        csv += `${stage.minHours || 0},`;
        csv += `${stage.within24Hours || 0},`;
        csv += `${stage.over24Hours || 0},`;
        csv += `${stage.over48Hours || 0},`;
        csv += `${stage.over72Hours || 0}\n`;
      });

      csv += '\n\n';

      // ========== 2. SUB-COUNTY PERFORMANCE RANKING ==========
      csv += '=== SUB-COUNTY PERFORMANCE RANKING ===\n';
      csv += 'Rank,Sub-County,Total Apps,Stage 1,Stage 1 Overdue,Stage 2,Stage 2 Overdue,Stage 3,Stage 3 Overdue,Stage 4,Stage 4 Overdue,Stage 5 Completed,Completed Apps,Pending Apps,Overdue Apps,Avg Completion Hours,Avg Pending Hours,Same-Day %,Completion Rate %\n';

      this.subCountyPerformance.forEach((county, index) => {
        csv += `${index + 1},`;
        csv += `"${county.subCountyName}",`;
        csv += `${county.totalApplications},`;
        csv += `${county.stage1_total || 0},`;
        csv += `${county.stage1_overdue || 0},`;
        csv += `${county.stage2_total || 0},`;
        csv += `${county.stage2_overdue || 0},`;
        csv += `${county.stage3_total || 0},`;
        csv += `${county.stage3_overdue || 0},`;
        csv += `${county.stage4_total || 0},`;
        csv += `${county.stage4_overdue || 0},`;
        csv += `${county.stage5_total || 0},`;
        csv += `${county.completedApps},`;
        csv += `${county.pendingApps},`;
        csv += `${county.overdueApps || 0},`;
        csv += `${county.avgCompletionHours || 0},`;
        csv += `${county.avgPendingHours || 0},`;
        csv += `${county.sameDayPercentage || 0},`;
        csv += `${county.completionRate || 0}\n`;
      });

      csv += '\n\n';

      // ========== 3. CRITICAL ALERTS - STUCK APPLICATIONS ==========
      csv += '=== CRITICAL ALERTS - STUCK APPLICATIONS (>24 Hours) ===\n';
      csv += 'Days Stuck,Hours Stuck,Stage,Stage Name,Sub-County,Ward,Business Name,Bill No,Business ID,Issuing Officer,Date Created,Comments\n';

      this.stuckApplications.forEach(app => {
        csv += `${app.daysStuck || 0},`;
        csv += `${app.hoursStuck || 0},`;
        csv += `${app.statusID},`;
        csv += `"${this.getStageNameDisplay(app.statusID)}",`;
        csv += `"${app.subCountyName || ''}",`;
        csv += `"${app.wardName || ''}",`;
        csv += `"${app.businessName || ''}",`;
        csv += `"${app.billNo || ''}",`;
        csv += `${app.businessID || ''},`;
        csv += `"${app.issuingOfficer || 'Not Assigned'}",`;
        csv += `"${app.dateCreated || ''}",`;
        csv += `"${(app.comments || '').replace(/"/g, '""')}"\n`;
      });

      csv += '\n\n';

      // ========== 4. DAILY TREND ANALYSIS ==========
      csv += '=== DAILY TREND ANALYSIS (Last 30 Days) ===\n';
      csv += 'Date,Day of Week,Total Apps,Same-Day Completion,Completion Rate %,Avg Completion Hours,Efficiency %\n';

      this.dailyTrend.forEach(day => {
        csv += `"${this.formatDateShort(day.applicationDate)}",`;
        csv += `"${this.getDayOfWeek(day.applicationDate)}",`;
        csv += `${day.totalApps || 0},`;
        csv += `${day.sameDayCompletion || 0},`;
        csv += `${this.calculateDayCompletionRate(day)},`;
        csv += `${day.avgCompletionHours || 0},`;
        csv += `${this.calculateEfficiency(day)}\n`;
      });

      csv += '\n\n';

      // ========== 5. PERFORMANCE SUMMARY STATISTICS ==========
      csv += '=== OVERALL PERFORMANCE SUMMARY ===\n';
      csv += 'Metric,Value\n';
      csv += `Total Applications (Period),${this.calculateTotalApplications()}\n`;
      csv += `Same-Day Completions,${this.calculateTotalCompletions()}\n`;
      csv += `Average Completion Time,${this.calculateAvgCompletionTime()}\n`;
      csv += `Overall Efficiency %,${this.calculateOverallEfficiency()}\n`;
      csv += `Total Critical Alerts,${this.stuckApplications.length}\n`;
      csv += `Total Sub-Counties,${this.subCountyPerformance.length}\n`;

      csv += '\n\n';
      csv += '=== END OF REPORT ===\n';

      return csv;
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounties");

      execute(data, authUrl)
          .then(res => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            }
          })
          .catch(error => console.error("Error loading sub-counties:", error));
    },

    selectedSubCounty(event) {
      if (event.target.value) {
        const data = JSON.parse(event.target.value);
        this.selectedSubCountyID = data.subCountyID;
      } else {
        this.selectedSubCountyID = null;
      }
    },

    // Styling Helper Methods
    getStageStatusClass(stage) {
      const stageId = parseInt(stage.statusID);
      const stageName = (stage.stageStatus || '').toLowerCase();

      if (stageId === 5 || stage.statusID === 5 || stage.statusID === '5' || stageName.includes('approv')) {
        return 'status-excellent';
      }

      const totalApps = parseInt(stage.totalApps) || 0;

      if (totalApps >= 50) return 'status-critical';
      if (totalApps >= 30) return 'status-danger';
      if (totalApps >= 15) return 'status-warning';
      if (totalApps >= 5) return 'status-good';
      return 'status-excellent';
    },

    getStageIcon(statusID) {
      const icons = {
        1: 'bx bx-file-blank',
        2: 'bx bx-money',
        3: 'bx bx-check-shield',
        4: 'bx bx-search-alt',
        5: 'bx bx-check-circle'
      };
      return icons[statusID] || 'bx bx-file';
    },

    getProgressClass(stage) {
      const onTimePercentage = this.getOnTimePercentage(stage);
      if (onTimePercentage >= 80) return 'bg-success';
      if (onTimePercentage >= 60) return 'bg-info';
      if (onTimePercentage >= 40) return 'bg-warning';
      return 'bg-danger';
    },

    getCompletionPercentage(stage) {
      if (!stage.totalApps) return 0;
      return ((stage.within24Hours || 0) / stage.totalApps) * 100;
    },

    getOnTimePercentage(stage) {
      if (!stage.totalApps) return 0;
      return ((stage.within24Hours || 0) / stage.totalApps) * 100;
    },

    getOverduePercentage(stage) {
      if (!stage.totalApps) return 0;
      return ((stage.over24Hours || 0) / stage.totalApps) * 100;
    },

    getStageNameDisplay(statusID) {
      const stageNames = {
        1: 'SBP Application',
        2: 'Bill Payment',
        3: 'Application Validation',
        4: 'Inspection',
        5: 'Approved'
      };
      return stageNames[statusID] || 'Unknown Stage';
    },

    getStageBadgeColor(statusID) {
      const colors = {
        1: 'bg-primary',
        2: 'bg-info',
        3: 'bg-warning',
        4: 'bg-danger',
        5: 'bg-success'
      };
      return colors[statusID] || 'bg-secondary';
    },

    getStageBoxClass(overdue, total) {
      if (!total || total === 0) return 'empty-stage';
      if (overdue > 0) return 'has-overdue';
      return 'all-ontime';
    },

    getRankClass(index) {
      if (index === 0) return 'rank-1';
      if (index === 1) return 'rank-2';
      if (index === 2) return 'rank-3';
      if (index >= this.subCountyPerformance.length - 3) return 'rank-danger';
      return '';
    },

    getPerformanceBadge(rate) {
      if (rate >= 80) return 'bg-success';
      if (rate >= 60) return 'bg-primary';
      if (rate >= 40) return 'bg-warning';
      return 'bg-danger';
    },

    getTimelineDotClass(stage) {
      const onTimePercentage = this.getOnTimePercentage(stage);
      if (onTimePercentage >= 80) return 'excellent';
      if (onTimePercentage >= 60) return 'good';
      if (onTimePercentage >= 40) return 'warning';
      return 'danger';
    },

    getStageBadgeClass(stage) {
      const onTimePercentage = this.getOnTimePercentage(stage);
      if (onTimePercentage >= 80) return 'bg-success';
      if (onTimePercentage >= 60) return 'bg-info';
      if (onTimePercentage >= 40) return 'bg-warning';
      return 'bg-danger';
    },

    getDangerLevel(hours) {
      if (!hours) return 'bg-info text-white';
      const hoursNum = parseFloat(hours);

      if (hoursNum > 720) return 'bg-dark text-white';
      if (hoursNum > 336) return 'bg-danger text-white';
      if (hoursNum > 168) return 'bg-danger text-white';
      if (hoursNum > 72) return 'bg-warning text-dark';
      if (hoursNum > 48) return 'bg-warning text-dark';
      return 'bg-info text-white';
    },

    getEfficiencyClass(day) {
      const efficiency = this.calculateEfficiency(day);
      if (efficiency >= 80) return 'bg-success';
      if (efficiency >= 60) return 'bg-info';
      if (efficiency >= 40) return 'bg-warning';
      return 'bg-danger';
    },

    getRowHighlight(day) {
      const efficiency = this.calculateEfficiency(day);
      if (efficiency >= 80) return 'table-success-subtle';
      if (efficiency >= 50) return '';
      return 'table-danger-subtle';
    },

    getApplicationCountBadge(count) {
      if (count >= 20) return 'bg-primary';
      if (count >= 10) return 'bg-info';
      return 'bg-secondary';
    },

    getCompletionRateBadge(day) {
      const rate = this.calculateDayCompletionRate(day);
      if (rate >= 80) return 'bg-success';
      if (rate >= 50) return 'bg-warning text-dark';
      return 'bg-danger';
    },

    // Calculation Methods
    calculateEfficiency(day) {
      if (!day.totalApps) return 0;
      return Math.round(((day.sameDayCompletion || 0) / day.totalApps) * 100);
    },

    calculateTotalApplications() {
      if (!this.dailyTrend || this.dailyTrend.length === 0) return 0;
      return this.dailyTrend.reduce((sum, day) => sum + (parseInt(day.totalApps) || 0), 0);
    },

    calculateTotalCompletions() {
      if (!this.dailyTrend || this.dailyTrend.length === 0) return 0;
      return this.dailyTrend.reduce((sum, day) => sum + (parseInt(day.sameDayCompletion) || 0), 0);
    },

    calculateAvgCompletionTime() {
      if (!this.dailyTrend || this.dailyTrend.length === 0) return 'N/A';
      const validDays = this.dailyTrend.filter(day => day.avgCompletionHours);
      if (validDays.length === 0) return 'N/A';
      const avgHours = validDays.reduce((sum, day) => sum + parseFloat(day.avgCompletionHours), 0) / validDays.length;
      return this.formatHours(avgHours);
    },

    calculateOverallEfficiency() {
      const total = this.calculateTotalApplications();
      const completions = this.calculateTotalCompletions();
      if (total === 0) return 0;
      return Math.round((completions / total) * 100);
    },

    calculateDayCompletionRate(day) {
      if (!day.totalApps) return 0;
      return Math.round(((day.sameDayCompletion || 0) / day.totalApps) * 100);
    },

    // Formatting Methods
    formatStuckDuration(hours) {
      if (!hours || hours === null) return 'Just now';

      const hoursNum = parseFloat(hours);

      if (hoursNum < 24) {
        return `${Math.floor(hoursNum)} hours`;
      }

      const days = Math.floor(hoursNum / 24);

      if (days < 7) {
        return days === 1 ? '1 day' : `${days} days`;
      }

      if (days < 30) {
        const weeks = Math.floor(days / 7);
        const remainingDays = days % 7;
        if (weeks === 1 && remainingDays === 0) return '1 week';
        if (weeks === 1 && remainingDays > 0) return `1 week ${remainingDays}d`;
        if (remainingDays === 0) return `${weeks} weeks`;
        return `${weeks} weeks ${remainingDays}d`;
      }

      const months = Math.floor(days / 30);
      const remainingDays = days % 30;

      if (months === 1 && remainingDays === 0) return '1 month';
      if (months === 1 && remainingDays > 0) return `1 month ${remainingDays}d`;
      if (remainingDays === 0) return `${months} months`;
      return `${months} months ${remainingDays}d`;
    },

    formatHours(hours) {
      if (!hours || hours === null) return 'N/A';
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (h < 24) return `${h}h ${m}m`;
      const days = Math.floor(h / 24);
      const remainingHours = h % 24;
      return `${days}d ${remainingHours}h`;
    },

    formatDaysStuck(days) {
      if (!days) return 'Just started';
      const d = Math.floor(days);
      const hours = Math.round((days - d) * 24);
      if (d === 0) return `${hours} hours`;
      if (d === 1) return `1 day ${hours}h`;
      return `${d} days ${hours}h`;
    },

    formattedDate(date) {
      return moment(date).format('DD MMM YYYY hh:mm A');
    },

    formatDateShort(date) {
      return moment(date).format('MMM DD, YYYY');
    },

    getDayOfWeek(date) {
      return moment(date).format('dddd');
    }
  }
};
</script>

<style scoped>
/* Performance Card Styles */
.performance-card {
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
  overflow: hidden;
}

.performance-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
}

/* Make all text white in performance cards */
.performance-card h6,
.performance-card .text-uppercase,
.performance-card .metric-badge,
.performance-card .sub-metric {
  color: white !important;
}

.performance-card .text-sm {
  color: rgba(255, 255, 255, 0.95) !important;
}

/* Status Color Classes */
.status-excellent {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-good {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.status-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.status-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.status-critical {
  background: linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%);
  color: white;
}

/* Metric Badge */
.metric-badge {
  font-size: 2.5rem;
  font-weight: 700;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
}

.sub-metric {
  font-size: 0.875rem;
  opacity: 0.9;
}

/* Stuck Application Alerts */
.stuck-alert {
  border-left: 4px solid #ef4444;
  background: #fef2f2;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.stuck-alert:hover {
  background: #fee2e2;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

/* Sub-County Ranking */
.subcounty-rank {
  border-left: 5px solid;
  padding-left: 1rem;
  margin-bottom: 1rem;
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.rank-1 { border-left-color: #10b981; }
.rank-2 { border-left-color: #3b82f6; }
.rank-3 { border-left-color: #f59e0b; }
.rank-danger { border-left-color: #ef4444; }

/* Stage Boxes */
.stage-box {
  border: 1px solid #e5e7eb;
  background: white;
  transition: all 0.2s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stage-box:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.stage-box.has-overdue {
  border-color: #ef4444;
  background: #fef2f2;
}

.stage-box.all-ontime {
  border-color: #10b981;
  background: #f0fdf4;
}

.stage-box.empty-stage {
  border-color: #e5e7eb;
  background: #f9fafb;
  opacity: 0.7;
}

/* Timeline Styles */
.timeline-stage {
  position: relative;
  padding-left: 2rem;
  padding-bottom: 1.5rem;
}

.timeline-stage::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 10px;
  bottom: -10px;
  width: 2px;
  background: #e5e7eb;
}

.timeline-dot {
  position: absolute;
  left: 0;
  top: 8px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid;
}

.timeline-dot.excellent { background: #10b981; border-color: #10b981; }
.timeline-dot.good { background: #3b82f6; border-color: #3b82f6; }
.timeline-dot.warning { background: #f59e0b; border-color: #f59e0b; }
.timeline-dot.danger { background: #ef4444; border-color: #ef4444; }

/* Stat Icon */
.stat-icon {
  font-size: 3rem;
  opacity: 0.2;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
}

/* Trend Stats */
.trend-stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 0.5rem;
  transition: transform 0.2s ease;
}

.trend-stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.trend-stat-icon {
  font-size: 2.5rem;
  margin-right: 1rem;
  opacity: 0.8;
}

.trend-stat-content h3 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.trend-stat-content p {
  font-size: 0.875rem;
  margin: 0;
}

/* Background Colors */
.bg-primary-subtle {
  background-color: #cfe2ff !important;
}

.bg-success-subtle {
  background-color: #d1fae5 !important;
}

.bg-info-subtle {
  background-color: #cff4fc !important;
}

.bg-warning-subtle {
  background-color: #fff3cd !important;
}

/* Table Highlights */
.table-success-subtle {
  background-color: #d1f2eb !important;
}

.table-danger-subtle {
  background-color: #f8d7da !important;
}

/* Card Shadows */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
  transition: all 0.3s ease-in-out;
  border: none;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075)!important;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .metric-badge {
    font-size: 2rem;
  }

  .stage-box {
    min-height: 100px;
  }

  .trend-stat-icon {
    font-size: 2rem;
  }
}

/* AI Insights Styles */
.ai-insights-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.performance-score-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.insight-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  height: 100%;
  border-left: 4px solid;
  transition: all 0.3s ease;
}

.insight-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.insight-card.critical {
  border-left-color: #ef4444;
}

.insight-card.warning {
  border-left-color: #f59e0b;
}

.insight-card.success {
  border-left-color: #10b981;
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  position: relative;
}

.insight-icon i {
  display: inline-block;
  font-style: normal;
}

/* Fallback if icon doesn't load */
.insight-icon i:empty::before {
  content: "⚠";
  font-size: 1.5rem;
}

.insight-card.critical .insight-icon i:empty::before {
  content: "🚨";
}

.insight-card.success .insight-icon i:empty::before {
  content: "🏆";
}

.insight-card.critical .insight-icon {
  background: #fee2e2;
  color: #ef4444;
}

.insight-card.warning .insight-icon {
  background: #fef3c7;
  color: #f59e0b;
}

.insight-card.success .insight-icon {
  background: #d1fae5;
  color: #10b981;
}

.insight-content h6 {
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-content p {
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.5;
}

.insight-content small {
  color: #6b7280;
}

.recommendations-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
}

.recommendations-section h6 {
  color: #1f2937 !important;
}

.recommendation-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.recommendation-card h6 {
  color: #1f2937;
  font-weight: 600;
}

.recommendation-card p {
  color: #4b5563;
}

.recommendation-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  transform: translateX(4px);
}

.recommendation-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.recommendation-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.quick-stat {
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  font-weight: 600;
}

.bg-info-subtle {
  background-color: #cff4fc !important;
}
</style>